import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';

import {
  DynamicField,
  DynamicFormDefinition,
  DynamicFormLayout,
  DynamicImportMapping,
} from '@models/dynamic-form.interface';
import { ApiService } from './api.service';

/** Wire format returned by GET /dynamicforms (list) — see FluidSysApi DynamicFormSummaryDto. */
interface DynamicFormSummaryApiDto {
  id: string;
  name: string;
  description?: string | null;
  pageTitle?: string | null;
  testTypeId?: number | null;
  layoutType: 'vertical' | 'grid';
  createdBy: string;
  createdOn: string;
  modifiedBy?: string | null;
  modifiedOn?: string | null;
}

/** Wire format returned by GET/POST/PUT /dynamicforms/{id} — see FluidSysApi DynamicFormDto. */
interface DynamicFormApiDto {
  id: string;
  name: string;
  description?: string | null;
  pageTitle?: string | null;
  testTypeId?: number | null;
  layoutType: 'vertical' | 'grid';
  selectedMappingId?: string | null;
  fields: DynamicField[];
  layout: DynamicFormLayout;
  importMappings: DynamicImportMapping[];
  createdBy: string;
  createdOn: string;
  modifiedBy?: string | null;
  modifiedOn?: string | null;
}

/** Wire format sent to POST/PUT /dynamicforms — see FluidSysApi SaveDynamicFormDto. */
interface SaveDynamicFormApiDto {
  name: string;
  description?: string;
  pageTitle?: string;
  testTypeId?: number | null;
  layoutType?: 'vertical' | 'grid';
  selectedMappingId?: string;
  fields?: DynamicField[];
  layout?: DynamicFormLayout;
  importMappings?: DynamicImportMapping[];
}

/** Wire format for a filled-in Dynamic Form instance — see FluidSysApi DynamicFormSubmissionDto. */
export interface DynamicFormSubmissionApiDto {
  id: string;
  dynamicFormId: string;
  jobRequestId?: number | null;
  testTypeId?: number | null;
  /** Dictionary<fieldId, value> — the submitted field values. */
  values: Record<string, unknown>;
  status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected';
  createdBy: string;
  createdOn: string;
  modifiedBy?: string | null;
  modifiedOn?: string | null;
}

/** Wire format sent to POST/PUT .../submissions — see FluidSysApi SaveDynamicFormSubmissionDto. */
export interface SaveDynamicFormSubmissionApiDto {
  jobRequestId?: number | null;
  testTypeId?: number | null;
  values?: Record<string, unknown>;
  status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected';
}

/** Marks a client-side draft that has not yet been persisted via the API. */
const DRAFT_ID_PREFIX = 'new-';

@Injectable({ providedIn: 'root' })
export class DynamicFormsService {
  private readonly api = inject(ApiService);

  private listCache$?: Observable<DynamicFormDefinition[]>;
  private readonly detailCache = new Map<string, Observable<DynamicFormDefinition | null>>();

  /** All (non-deleted) dynamic forms — summary-shaped, exposed as DynamicFormDefinition for template compatibility. */
  public get forms$(): Observable<DynamicFormDefinition[]> {
    if (!this.listCache$) {
      this.listCache$ = this.api.get('dynamicforms').pipe(
        map((summaries: DynamicFormSummaryApiDto[]) => summaries.map(mapSummaryToDefinition)),
        shareReplay(1)
      );
    }

    return this.listCache$;
  }

  public search(params: {
    term?: string;
    layout?: 'all' | 'vertical' | 'grid';
    sort?: 'nameAsc' | 'nameDesc' | 'modifiedDesc' | 'modifiedAsc';
  }): Observable<DynamicFormDefinition[]> {
    const query = new URLSearchParams();
    if (params.term) query.set('term', params.term);
    if (params.layout) query.set('layout', params.layout);
    if (params.sort) query.set('sort', params.sort);

    const queryString = query.toString();
    const path = queryString ? `dynamicforms?${queryString}` : 'dynamicforms';

    return this.api
      .get(path)
      .pipe(map((summaries: DynamicFormSummaryApiDto[]) => summaries.map(mapSummaryToDefinition)));
  }

  public getById(id: string): Observable<DynamicFormDefinition | null> {
    if (!id) {
      return of(null);
    }

    if (!this.detailCache.has(id)) {
      const request$ = this.api.get(`dynamicforms/${id}`).pipe(
        map((dto: DynamicFormApiDto) => mapApiDtoToDefinition(dto)),
        catchError(() => of(null)),
        shareReplay(1)
      );
      this.detailCache.set(id, request$);
    }

    return this.detailCache.get(id)!;
  }

  public createDraft(): DynamicFormDefinition {
    const now = new Date().toISOString();
    return {
      id: `${DRAFT_ID_PREFIX}${Math.random().toString(36).slice(2, 9)}`,
      name: 'New Dynamic Form',
      description: '',
      pageTitle: 'New Dynamic Form',
      testTypeId: '',
      createdByName: 'UI User',
      modifiedByName: 'UI User',
      createdOn: now,
      modifiedOn: now,
      selectedMappingId: '',
      fields: [],
      layout: {
        type: 'vertical',
        sections: [
          {
            id: 'section-main',
            title: 'Main Section',
            columns: 1,
            fieldIds: [],
          },
        ],
        tables: [],
      },
      importMappings: [],
    };
  }

  public save(form: DynamicFormDefinition): Observable<DynamicFormDefinition> {
    const dto = mapDefinitionToSaveDto(form);
    const isDraft = form.id.startsWith(DRAFT_ID_PREFIX);

    const request$ = isDraft ? this.api.post('dynamicforms', dto) : this.api.put(`dynamicforms/${form.id}`, dto);

    return request$.pipe(
      map((saved: DynamicFormApiDto) => mapApiDtoToDefinition(saved)),
      tap((saved) => this.invalidateCache(saved.id))
    );
  }

  public delete(id: string): Observable<boolean> {
    return this.api.delete(`dynamicforms/${id}`).pipe(
      map(() => true),
      tap(() => this.invalidateCache(id))
    );
  }

  // ── Submissions ────────────────────────────────────────────────────────────

  public getSubmissionsByFormId(formId: string): Observable<DynamicFormSubmissionApiDto[]> {
    return this.api.get(`dynamicforms/${formId}/submissions`);
  }

  public getSubmissionById(id: string): Observable<DynamicFormSubmissionApiDto | null> {
    return this.api.get(`dynamicform-submissions/${id}`).pipe(catchError(() => of(null)));
  }

  public createSubmission(
    formId: string,
    dto: SaveDynamicFormSubmissionApiDto
  ): Observable<DynamicFormSubmissionApiDto> {
    return this.api.post(`dynamicforms/${formId}/submissions`, dto);
  }

  public updateSubmission(
    id: string,
    dto: SaveDynamicFormSubmissionApiDto
  ): Observable<DynamicFormSubmissionApiDto> {
    return this.api.put(`dynamicform-submissions/${id}`, dto);
  }

  public deleteSubmission(id: string): Observable<boolean> {
    return this.api.delete(`dynamicform-submissions/${id}`).pipe(map(() => true));
  }

  private invalidateCache(id?: string): void {
    this.listCache$ = undefined;
    if (id) {
      this.detailCache.delete(id);
    }
  }
}

function mapSummaryToDefinition(dto: DynamicFormSummaryApiDto): DynamicFormDefinition {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description ?? undefined,
    testTypeId: dto.testTypeId != null ? String(dto.testTypeId) : undefined,
    pageTitle: dto.pageTitle ?? '',
    createdByName: dto.createdBy,
    modifiedByName: dto.modifiedBy ?? undefined,
    createdOn: dto.createdOn,
    modifiedOn: dto.modifiedOn ?? undefined,
    fields: [],
    layout: {
      type: dto.layoutType,
      sections: [],
      tables: [],
    },
    importMappings: [],
  };
}

function mapApiDtoToDefinition(dto: DynamicFormApiDto): DynamicFormDefinition {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description ?? undefined,
    testTypeId: dto.testTypeId != null ? String(dto.testTypeId) : undefined,
    pageTitle: dto.pageTitle ?? '',
    createdByName: dto.createdBy,
    modifiedByName: dto.modifiedBy ?? undefined,
    createdOn: dto.createdOn,
    modifiedOn: dto.modifiedOn ?? undefined,
    selectedMappingId: dto.selectedMappingId ?? undefined,
    fields: dto.fields ?? [],
    layout: dto.layout ?? { type: dto.layoutType, sections: [], tables: [] },
    importMappings: dto.importMappings ?? [],
  };
}

function mapDefinitionToSaveDto(form: DynamicFormDefinition): SaveDynamicFormApiDto {
  const testTypeId = form.testTypeId ? Number(form.testTypeId) : undefined;

  return {
    name: form.name,
    description: form.description || undefined,
    pageTitle: form.pageTitle || undefined,
    testTypeId: testTypeId !== undefined && !Number.isNaN(testTypeId) ? testTypeId : undefined,
    layoutType: form.layout?.type ?? 'vertical',
    selectedMappingId: form.selectedMappingId || undefined,
    fields: form.fields ?? [],
    layout: form.layout ?? { type: 'vertical', sections: [], tables: [] },
    importMappings: form.importMappings ?? [],
  };
}
